import { useCallback, useContext, type FunctionComponent } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { asMoney, forTemplate } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import type { BillingIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { PlanComparisonContext } from '../PlanComparisonContext';
import type { BillingQuotes } from '../types';

import styles from './PlanComparisonStandardHeaderCell.less';

export interface PlanComparisonStandardHeaderCellProps {
  teamId: string;
  teamName: string;
  isMonthly: boolean;
  prices?: BillingQuotes['standardPrices'];
  isInPPCohort?: boolean;
}

const format = forTemplate('billing_page_one');

export const PlanComparisonStandardHeaderCell: FunctionComponent<
  PlanComparisonStandardHeaderCellProps
> = ({ teamId, isMonthly, prices, isInPPCohort }) => {
  const { variation, onChooseStandard } = useContext(PlanComparisonContext);

  const productCode = isMonthly
    ? prices?.monthly.productCode
    : prices?.annual.productCode;
  const pricePerUser = isMonthly
    ? prices?.monthly.subTotalPerUser
    : prices?.annual.subTotalPerUser;
  const frequency = format(isMonthly ? 'per-month' : 'per-year');

  const onClick = useCallback(() => {
    if (variation === 'end-of-trial-friction-overlay') {
      Analytics.sendClickedButtonEvent({
        buttonName: 'chooseStandardButton',
        source: 'trelloPlanPricingModal',
        attributes: {
          viaEndOfTrial: true,
        },
      });
    }

    if (variation === 'billing-page') {
      Analytics.sendClickedButtonEvent({
        buttonName: 'upgradeToStandardButton',
        source: 'planComparisonSection',
        containers: {
          organization: {
            id: teamId,
          },
        },
      });
    }

    onChooseStandard?.(productCode!);
  }, [productCode, onChooseStandard, teamId, variation]);

  return (
    <th
      className={styles.planCell}
      data-value={format('best-value')}
      data-testid={getTestId<BillingIds>('plan-comparison-cell-standard')}
    >
      <h3 className={styles.name}>{format('standard-name')}</h3>
      <p className={styles.info}>
        {isInPPCohort
          ? format('standard-description-personal-productivity')
          : format('standard-description-repackaging-gtm')}
      </p>
      <span className={styles.price}>
        {format('amount-usd', {
          amount: asMoney(pricePerUser || 0).replace(/\.00$/, ''),
        }).replace(/\s/g, ' ')}
      </span>
      <p className={styles.frequency}>
        {format('per-user')}
        <br />
        {frequency}
      </p>
      <Button
        key={
          variation === 'billing-page' ? `upgrade-standard` : `modal-standard`
        }
        onClick={onClick}
        testId={
          variation === 'billing-page'
            ? getTestId<BillingIds>('upgrade-team-standard-button')
            : getTestId<BillingIds>('get-standard')
        }
      >
        {variation === 'reverse-trial-overlay' || variation === 'billing-page'
          ? format('upgrade-now')
          : format('get-standard')}
      </Button>
    </th>
  );
};
